import { CommonModule } from "@angular/common";
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatButtonModule } from "@angular/material/button";
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import { MatCardModule } from "@angular/material/card";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatChipsModule } from "@angular/material/chips";
import { MatNativeDateModule, MatRippleModule } from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatDialogModule } from "@angular/material/dialog";
import { MatDividerModule } from "@angular/material/divider";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatListModule } from "@angular/material/list";
import { MatMenuModule } from "@angular/material/menu";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatRadioModule } from "@angular/material/radio";
import { MatSelectModule } from "@angular/material/select";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { MatStepperModule } from "@angular/material/stepper";
import { MatTableModule } from "@angular/material/table";
import { MatTabsModule } from "@angular/material/tabs";
import { MatTooltipModule } from "@angular/material/tooltip";
import { CameraModule } from "@cotecna/camera";
import { DirectivesModule } from "@cotecna/directives";
import { DynamicViewEngineModule } from "@cotecna/dynamic-view-engine";
import { FeatureManagerModule } from "@cotecna/feature-manager";
import { FileManagementModule } from "@cotecna/file-management";
import { FabSpeedDialModule } from "@cotecna/material";
import { SidemenuModule, ToolbarModule } from "@cotecna/shell";
import { TablesModule } from "@cotecna/tables";
import { TabsModule } from "@cotecna/tabs";
import { DBConfig, NgxIndexedDBModule } from "ngx-indexed-db";
import { PowerBIEmbedModule } from "powerbi-client-angular";
import { ChartModule } from "primeng/chart";

import { ItInspectorsModule } from "../inspectors/inspectors.module";
import { ItemsModule } from "../items/items.module";
import { ScopeOfWorkModule } from "../scope-of-work/scope-of-work.module";
import { DashboardsComponent } from "./dashboards/dashboards.component";
import { InspectionDetailsModule } from "./details/inspection-details.module";
import { AddDocumentComponent } from "./dialogs/add-document/add-document.component";
import { AddFormDialogComponent } from "./dialogs/add-form-dialog/add-form-dialog.component";
import { AddPhotosComponent } from "./dialogs/add-photos/add-photos.component";
import { AdditionalFiltersDialogComponent } from "./dialogs/additional-filters-dialog/additional-filters-dialog.component";
import { AdditionalInfoDialogComponent } from "./dialogs/additional-info-dialog/additional-info-dialog.component";
import { CloneInspectionDialogComponent } from "./dialogs/clone-inspection/clone-inspection-dialog/clone-inspection-dialog.component";
import { EditInspectorTeamsComponent } from "./dialogs/edit-inspectors-team/edit-inspector-teams.component";
import { FinishInspectionDialogComponent } from "./dialogs/finish-inspection-dialog/finish-inspection-dialog.component";
import { FullActivityDialogComponent } from "./dialogs/full-activity-dialog/full-activity-dialog.component";
import { GenerateDocumentDialogComponent } from "./dialogs/generate-document-dialog/generate-document-dialog.component";
import { GeneratePicturesReportDialogComponent } from "./dialogs/generate-pictures-report-dialog/generate-pictures-report-dialog.component";
import { ImportDialogComponent } from "./dialogs/import-dialog/import-dialog.component";
import { ImportEventDialogComponent } from "./dialogs/import-event-dialog/import-event-dialog.component";
import { ItemSectionsDialogComponent } from "./dialogs/item-sections-dialog/item-sections-dialog.component";
import { LoadingComponent } from "./dialogs/loading/loading.component";
import { DisplayMissingRequiredFields } from "./dialogs/missing-required-fields/display-missing-required-fields/display-missing-required-fields.component";
import { MissingRequiredFieldsComponent } from "./dialogs/missing-required-fields/missing-required-fields.component";
import { MultiofficeDialogComponent } from "./dialogs/multioffice-dialog/multioffice-dialog.component";
import { PicturesReportConfigDialogComponent } from "./dialogs/pictures-report-config-dialog/pictures-report-config-dialog.component";
import { ServiceProviderCoordinatorDialogComponent } from "./dialogs/service-provider-coordinator-dialog/service-provider-coordinator-dialog.component";
import { EventDialogComponent } from "./dialogs/timelog-dialog/event-dialog.component";
import { EmbeddedDashboardComponent } from "./embedded-dashboard/embedded-dashboard.component";
import { HomeComponent } from "./home/home.component";
import { InspectionRoutingModule } from "./inspection-routing.module";
import { AddInspectionButtonComponent } from "./inspections/add-inspection-button/add-inspection-button.component";
import { FiltersComponent } from "./inspections/filters/filters.component";
import { InspectionsComponent } from "./inspections/inspections.component";
import { SearchDialogComponent } from "./inspections/search-component/search-dialog.component";
import { AdditionalInfoInputComponent } from "./new-inspection/additional-info-input/additional-info-input.component";
import { GeneralInfoComponent } from "./new-inspection/general-info/general-info.component";
import { NewInspectionComponent } from "./new-inspection/new-inspection.component";

const dbConfig: DBConfig = {
  name: "Eye",
  version: 1,
  objectStoresMeta: [
    {
      store: "photoCache",
      storeConfig: { keyPath: "filename", autoIncrement: false },
      storeSchema: [
        { name: "dataUrl", keypath: "dataUrl", options: { unique: false } },
      ],
    },
  ],
};

@NgModule({
  declarations: [
    InspectionsComponent,
    NewInspectionComponent,
    GeneralInfoComponent,
    AdditionalInfoInputComponent,
    LoadingComponent,
    SearchDialogComponent,
    AddDocumentComponent,
    AddPhotosComponent,
    CloneInspectionDialogComponent,
    EventDialogComponent,
    HomeComponent,
    FinishInspectionDialogComponent,
    DashboardsComponent,
    EditInspectorTeamsComponent,
    AdditionalInfoDialogComponent,
    MissingRequiredFieldsComponent,
    DisplayMissingRequiredFields,
    AddInspectionButtonComponent,
    ImportDialogComponent,
    AddFormDialogComponent,
    GenerateDocumentDialogComponent,
    FiltersComponent,
    MultiofficeDialogComponent,
    EmbeddedDashboardComponent,
    ItemSectionsDialogComponent,
    FullActivityDialogComponent,
    ServiceProviderCoordinatorDialogComponent,
    ImportEventDialogComponent,
    AdditionalFiltersDialogComponent,
    GeneratePicturesReportDialogComponent,
    PicturesReportConfigDialogComponent,
  ],
  imports: [
    NgxIndexedDBModule.forRoot(dbConfig),
    CommonModule,
    InspectionRoutingModule,
    DynamicViewEngineModule,
    MatStepperModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    MatSelectModule,
    MatIconModule,
    MatDatepickerModule,
    MatDividerModule,
    MatListModule,
    MatDialogModule,
    MatMenuModule,
    MatSnackBarModule,
    MatTableModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatAutocompleteModule,
    InspectionDetailsModule,
    ItInspectorsModule,
    ToolbarModule,
    FormsModule,
    CameraModule,
    FileManagementModule,
    FeatureManagerModule,
    MatButtonToggleModule,
    FabSpeedDialModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    SidemenuModule,
    TabsModule,
    TablesModule,
    DirectivesModule,
    ChartModule,
    MatTabsModule,
    ItemsModule,
    ScopeOfWorkModule,
    ReactiveFormsModule,
    MatChipsModule,
    MatRadioModule,
    PowerBIEmbedModule,
    MatNativeDateModule,
    MatRippleModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class InspectionModule {}
